// 忽略 Element Plus Dialog/Table 等组件触发的 ResizeObserver 循环警告
// webpack-dev-server overlay 用 addEventListener 捕获，必须在捕获阶段拦截
window.addEventListener('error', (e) => {
    if (e.message && typeof e.message === 'string' && e.message.includes('ResizeObserver loop')) {
        e.stopImmediatePropagation()
        e.preventDefault()
    }
}, true)

import { createApp } from 'vue'
import { tip } from '@/directives/tip'
import App from './App.vue'
import router from './router'  // 引入路由配置
import axios from 'axios'
import { clearTcmClinicalSessionStorage } from '@/utils/tcmClinicalHandoff'
import { currentMessageRequest, messageAuthSession, staleMessageRequest } from '@/utils/messageAuthSession'

// 引入 Element Plus 核心和样式
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
// Load shared design tokens after Element Plus so font and color overrides apply.
import './styles/variables.css'
import './styles/bios-tokens.css'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
// 引入 Element Plus 消息提示
import { ElMessage } from 'element-plus'
// 引入图标库（全局注册所有图标，方便使用）
import * as ElementPlusIconsVue from '@element-plus/icons-vue'

const app = createApp(App)

app.use(router)

// 全局配置 axios（关键）
app.config.globalProperties.$axios = axios  // 全局挂载，组件内用 this.$axios 调用

// 统一使用相对路径，开发环境通过 vue.config.js 的 devServer.proxy 转发到后端
axios.defaults.baseURL = ''
axios.defaults.timeout = 300000  // 请求超时时间

// 请求拦截：自动附加 Token
axios.interceptors.request.use(config => {
    if (!currentMessageRequest(config)) throw staleMessageRequest()
    const token = localStorage.getItem('token')
    const existingAuthorization = config.headers?.Authorization
        || config.headers?.authorization
        || (typeof config.headers?.get === 'function' ? config.headers.get('Authorization') : '')
    if (token && !existingAuthorization && !config.skipAuthHeader) {
        if (typeof config.headers?.set === 'function') {
            config.headers.set('Authorization', `Bearer ${token}`)
        } else {
            config.headers.Authorization = `Bearer ${token}`
        }
    }
    return config
})

// 响应拦截：401 时尝试刷新 Token
let isRefreshing = false
let pendingRequests = []
let hasRedirectedToLogin = false

const redirectToLogin = () => {
    pendingRequests.forEach(({ reject }) => reject(new Error('Token expired')))
    pendingRequests = []
    clearTcmClinicalSessionStorage()
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    if (!hasRedirectedToLogin) {
        hasRedirectedToLogin = true
        ElMessage.error('登录已过期，请重新登录')
    }
    if (router.currentRoute.value.name !== 'Login') {
        router.push({ name: 'Login', query: { redirect: router.currentRoute.value.fullPath } })
    }
}

axios.interceptors.response.use(
    response => {
        if (!currentMessageRequest(response.config)) return Promise.reject(staleMessageRequest())
        if (response.data?.code === 401 && !response.config?.skipAuthRefresh) {
            redirectToLogin()
            return Promise.reject(new Error(response.data.message || 'Token expired'))
        }
        // 后端业务错误：HTTP 200 但 code !== 200，转为 rejected promise
        if (response.data?.code !== undefined && response.data.code !== 200) {
            const err = new Error(response.data.message || '操作失败')
            err.response = response
            err.isBusinessError = true
            return Promise.reject(err)
        }
        return response
    },
    async error => {
        const originalRequest = error.config
        if (!currentMessageRequest(originalRequest)) return Promise.reject(staleMessageRequest())
        // Recovery owns credential renewal and identity guards; do not replay it here.
        if (originalRequest?.skipAuthRefresh) return Promise.reject(error)
        const isRefreshRequest = originalRequest?.url?.includes('/api/auth/refresh')
        if (error.response?.status === 429) {
            const retryAfter = error.response.headers?.['retry-after']
            const message = error.response.data?.message
                || (retryAfter ? `请求过于频繁，请稍后 ${retryAfter} 秒再试` : '请求过于频繁，请稍后再试')
            const err = new Error(message)
            err.response = error.response
            return Promise.reject(err)
        }
        if (error.response?.status === 401 && isRefreshRequest) {
            redirectToLogin()
            return Promise.reject(new Error('Token expired'))
        }
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // 排队等待刷新完成
                return new Promise((resolve, reject) => {
                    pendingRequests.push({ resolve, reject, originalRequest })
                })
            }
            originalRequest._retry = true
            isRefreshing = true
            const refreshSession = messageAuthSession()
            try {
                const rt = localStorage.getItem('refreshToken')
                if (!rt) throw new Error('No refresh token')
                const res = await axios.post('/api/auth/refresh', null, {
                    headers: { Authorization: `Bearer ${rt}` }, messageSession: refreshSession,
                })
                if (refreshSession !== messageAuthSession()) throw staleMessageRequest()
                const newToken = res.data.data.token
                localStorage.setItem('token', newToken)
                window.dispatchEvent(new Event('gutdb-auth-token-updated'))
                // 重放排队请求
                pendingRequests.forEach(({ resolve, reject, originalRequest }) => {
                    if (!currentMessageRequest(originalRequest)) { reject(staleMessageRequest()); return }
                    originalRequest.headers = originalRequest.headers || {}
                    originalRequest.headers.Authorization = `Bearer ${newToken}`
                    resolve(axios(originalRequest))
                })
                pendingRequests = []
                originalRequest.headers = originalRequest.headers || {}
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return axios(originalRequest)
            } catch {
                if (refreshSession !== messageAuthSession()) {
                    pendingRequests.forEach(({ reject }) => reject(staleMessageRequest()))
                    pendingRequests = []
                    return Promise.reject(staleMessageRequest())
                }
                // 刷新失败，跳转登录
                redirectToLogin()
                return Promise.reject(new Error('Token expired'))
            } finally {
                isRefreshing = false
            }
        }
        return Promise.reject(error)
    }
)

// 全局注册所有图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component)
}

// 全局使用 Element Plus
app.use(ElementPlus, { locale: zhCn })

// 初始化埋点 SDK（必须在 router 注册之后、mount 之前）
import { initTracker, vTrack } from '@/utils/tracker'
app.directive('track', vTrack)
app.directive('tip', tip)
initTracker(app, router)

app.mount('#app')
